import defaultDiscard from '@redux-offline/redux-offline/lib/defaults/discard';
import logging from '@sstdev/lib_logging';
import NotLoggedInError from '../errors/NotLoggedInError';
import isNetworkError from '../http/isNetworkError';
import { MAX_DECAY_STEPS } from './retryOverride';
import { boundedAttemptsFor, recordBoundedFailure } from './attemptCounts';

/**
 * Discarding rolls the action back, reverting the optimistic local record and marking it
 * `clientSendFailure`, so a write is dropped only when sending it again cannot succeed. Nothing
 * currently surfaces that status to the user. The split below is between a
 * request that never reached the application, which is retried indefinitely, and one the
 * application answered about, which is bounded at the length of the back off schedule.
 *
 * The bound has to exist because the outbox is strictly FIFO - an action that can never succeed
 * freezes everything queued behind it. It is checked above the status branches so a status added
 * later cannot reintroduce an unbounded path.
 */

/**
 * The application never ran, so nothing about the write itself is wrong. 502/504 come from a proxy
 * or load balancer, 503 from a service that is up but refusing work - a rolling deploy or an
 * outage. None of them are specific to this action, so all of them resolve on their own, and an
 * outage outlasting the schedule must not cost the user their queued work.
 */
const GATEWAY_STATUSES = [502, 503, 504];

export default function discard(error, action, retriesFromOutbox) {
    // ---- Unbounded: the request never reached the application. ----
    // Not an HTTP failure at all: offlineEffectOverride throws this when the user is logged out or
    // is busy with a dirty form, to defer the Auth0 redirect rather than yank them out of their
    // work. Nothing was sent. Deliberately unbounded - the write has to outlive the login.
    if (error instanceof NotLoggedInError) return false;
    if (action.stopDueToUnexpectedError) return true;
    // fetch itself failed, so the device is offline, behind a captive portal, or on an access point
    // that cannot reach us. Our own online check can lag reality - it only re-evaluates on browser
    // network events and socket changes - which is how a queued write gets sent at all. Keep it
    // queued until the network returns; discarding here is how a shift of offline scanning is lost.
    if (isNetworkError(error)) return false;
    if (GATEWAY_STATUSES.includes(error.status)) return false;

    // Only failures that get past the returns above spend the bound, and only the ones being kept
    // for another attempt: charged after the decision, so this attempt is not counted against
    // itself. An outage or a shift offline therefore cannot leave a write with its budget already
    // spent.
    const discarding = discardAnsweredFailure(error, action, retriesFromOutbox);
    if (!discarding) recordBoundedFailure(action);
    return discarding;
}

/**
 * The application answered about this write, so bound it at the length of the back off schedule.
 */
function discardAnsweredFailure(error, action, retriesFromOutbox) {
    // Charged to this action rather than to the outbox, so a write that was parked while another
    // tenant's work was sent does not arrive already out of budget. See attemptCounts.js.
    const retries = boundedAttemptsFor(action, retriesFromOutbox);

    // Checked before any status is examined, so it holds for statuses with a branch below, statuses
    // with none, and statuses nobody has thought of yet.
    if (retries >= MAX_DECAY_STEPS) {
        logging.error(
            `Status ${error.status} survived ${retries} progressive back offs. Discarding "${action?.type}".`
        );
        return true;
    }
    // Throttling, not rejection: the server is telling us to come back later, so it has to be
    // retried even though defaultDiscard would drop it for being a 4xx. A rate limit that outlasts
    // the schedule is a wall, not throttling, and the bound above catches it.
    if (error.status === 429) return false;
    if (error.status === 401 && retries < 3) {
        //just in case, if this was due to unfortunate timing with logging out,
        //do allow to requeue and therefore retry when they get back online
        return false;
    }
    if (error.status === 403) {
        // User is not authorized to access this resource.
        // Retrying will not help, so discard immediately.
        return true;
    }

    return (defaultDiscard.default || defaultDiscard)(error, action, retries);
}
